import { useCallback, useEffect, useMemo, useState } from 'react';
import StripeLanguageNote from '../i18n/StripeLanguageNote';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PLAN_LABEL, type PlanCode } from '../subscription/price';
import {
  ADD_ON_INFO,
  INTERVALS,
  fetchOffers,
  fetchStatus,
  money,
  refreshEntitlements,
  type AddOnCode,
  type AiCode,
  type Interval,
  type Offer,
  type SubscriptionStatus,
} from '../subscription/offers';
import { useLearner } from '../learners/LearnerContext';
import PasswordDialog from '../components/PasswordDialog';
import StorageUsage from '../media/StorageUsage';
import { translate, useT } from '../i18n/i18n';
import { refreshAiAccess, useAiAccess } from '../ai/useAiAccess';
import CreditsBar from '../ai/CreditsBar';

const STATUS_LABEL: Record<string, string> = {
  TRIALING: 'Trial',
  ACTIVE: 'Active',
  PAST_DUE: 'Payment overdue',
  CANCELED: 'Cancelled',
  EXPIRED: 'Ended',
};

const ADD_ONS: AddOnCode[] = ['CUSTOM_MEDIA', 'TALK_BOARD'];
type Choice = PlanCode | 'NONE';

function describeRemaining(endMs: number): string {
  const ms = endMs - Date.now();
  if (ms <= 0) return translate('ended');
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(ms / day);
  const hours = Math.floor((ms % day) / (60 * 60 * 1000));
  if (days >= 1) {
    if (hours > 0) {
      return days === 1
        ? translate('{days} day, {hours} h remaining', { days, hours })
        : translate('{days} days, {hours} h remaining', { days, hours });
    }
    return days === 1 ? translate('{days} day remaining', { days }) : translate('{days} days remaining', { days });
  }
  if (hours >= 1) return hours === 1 ? translate('{hours} hour remaining', { hours }) : translate('{hours} hours remaining', { hours });
  return translate('less than an hour remaining');
}

const cycleLabel = (c: Interval) => {
  const label = INTERVALS.find((i) => i.value === c)?.label;
  return label ? translate(label).toLowerCase() : c;
};
const everyOf = (c: Interval) => translate(INTERVALS.find((i) => i.value === c)?.every ?? 'month');

export default function SubscriptionSection() {
  const t = useT();
  const { setSubscriptionStatus } = useAuth();
  const { learners } = useLearner();
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [interval, setInterval] = useState<Interval>('MONTHLY');
  const [plan, setPlan] = useState<Choice>('STANDARD');
  const [addOns, setAddOns] = useState<AddOnCode[]>([]);
  const [aiTier, setAiTier] = useState<AiCode | 'NONE'>('NONE');
  const [packs, setPacks] = useState<Record<string, number>>({});
  const { access: ai } = useAiAccess();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pending, setPending] = useState<'subscribe' | 'trial' | 'change' | 'portal' | 'addons' | 'cycle' | 'ai' | 'packs' | null>(null);
  const [nextCycle, setNextCycle] = useState<Interval>('MONTHLY');
  const busy = pending !== null;
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<{ code: string; percentOff: number } | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [promosRunning, setPromosRunning] = useState(false);

  useEffect(() => {
    api<{ available: boolean }>('/promo/available', { token: true })
      .then((r) => setPromosRunning(r.available))
      .catch(() => setPromosRunning(false));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      refreshEntitlements();
      const [s, list] = await Promise.all([fetchStatus(), fetchOffers().catch(() => [] as Offer[])]);
      setStatus(s);
      setSubscriptionStatus(s.status);
      setOffers(list);
      if (s.status === 'ACTIVE') {
        setInterval(s.interval);
        setNextCycle(s.pendingInterval ?? s.interval);
        setPlan(s.mainPlan ? (s.plan as PlanCode) : 'NONE');
        setAddOns(s.addOns);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('Could not load your subscription.'));
    } finally {
      setLoading(false);
    }
  }, [setSubscriptionStatus]);

  useEffect(() => { void load(); }, [load]);

  const offerFor = (product: string, at: Interval) => offers.find((o) => o.product === product && o.interval === at) ?? null;
  const plansHere = useMemo(() => offers.filter((o) => o.type === 'PLAN' && o.interval === interval), [offers, interval]);
  const cyclesOffered = INTERVALS.filter((i) => offers.some((o) => o.interval === i.value));
  const every = t(INTERVALS.find((i) => i.value === interval)?.every ?? 'month');
  const planPlaces = plan !== 'NONE' ? offerFor(plan, interval)?.maxLearners ?? null : null;
  const seats = planPlaces ?? Math.max(1, learners.length);
  const seatLabel = planPlaces
    ? (seats === 1 ? t('{n} place on the plan', { n: seats }) : t('{n} places on the plan', { n: seats }))
    : (seats === 1 ? t('{n} learner', { n: seats }) : t('{n} learners', { n: seats }));
  const cap = (max: number) => (max === 1 ? t('1 learner') : t('up to {n} learners', { n: max }));

  useEffect(() => {
    if (plan === 'NONE') setAddOns((a) => a.filter((x) => !ADD_ON_INFO[x].needsMain));
  }, [plan]);

  const aiOn = !!ai?.canBuy && offers.some((o) => o.type === 'AI');
  const aiCurrent: AiCode | 'NONE' = status?.status === 'ACTIVE' && ai?.balance.tier?.startsWith('AI_') ? (ai.balance.tier as AiCode) : 'NONE';
  useEffect(() => { if (status?.status === 'ACTIVE') setAiTier(aiCurrent); }, [status?.status, aiCurrent]);
  const aiInfo = (code: string) => ai?.offers.find((o) => o.id === code) ?? null;
  const aiTiersHere = offers.filter((o) => o.type === 'AI' && o.interval === interval);
  const packOffers = ai?.offers.filter((o) => !o.monthly) ?? [];
  const packLines = packOffers.filter((p) => (packs[p.id] ?? 0) > 0)
    .map((p) => ({ label: t('{n} × {pack} (paid once)', { n: packs[p.id], pack: t(p.label) }), amount: p.pence * packs[p.id], currency: 'gbp' }));
  const packTotal = packLines.reduce((n, l) => n + l.amount, 0);
  const packList = packOffers.flatMap((p) => Array.from({ length: packs[p.id] ?? 0 }, () => p.id));

  const lines = useMemo(() => {
    const out: { label: string; amount: number; currency: string }[] = [];
    if (plan !== 'NONE') {
      const o = offerFor(plan, interval);
      if (o) out.push({ label: t('{plan} plan', { plan: t(PLAN_LABEL[plan]) }), amount: o.unitAmount, currency: o.currency });
    }
    for (const a of addOns) {
      const o = offerFor(a, interval);
      if (o) out.push({ label: t('{addOn} × {seats}', { addOn: t(ADD_ON_INFO[a].name), seats: seatLabel }), amount: o.unitAmount * seats, currency: o.currency });
    }
    if (aiTier !== 'NONE') {
      const o = offerFor(aiTier, interval);
      const info = aiInfo(aiTier);
      if (o) out.push({ label: t('{plan} ({credits} credits a month)', { plan: t(info?.label ?? aiTier), credits: info?.credits ?? 0 }), amount: o.unitAmount, currency: o.currency });
    }
    return out;
  }, [plan, addOns, aiTier, interval, offers, seats, seatLabel, t, ai]);
  const total = lines.reduce((t, l) => t + l.amount, 0);
  const firstPayment = total + packTotal;
  const discount = promo ? Math.round((firstPayment * promo.percentOff) / 100) : 0;

  async function applyPromo() {
    setCheckingPromo(true);
    setPromoError(null);
    try {
      setPromo(await api<{ code: string; percentOff: number }>(`/promo/check?code=${encodeURIComponent(promoInput.trim())}`, { token: true }));
    } catch (e) {
      setPromo(null);
      setPromoError(e instanceof Error ? e.message : t('That code could not be checked.'));
    } finally {
      setCheckingPromo(false);
    }
  }
  const currency = lines[0]?.currency ?? 'gbp';

  function follow(url: string) {
    if (/^https?:\/\//i.test(url)) window.location.assign(url);
    else setInfo(t('Payment is not set up yet. Please try again later.'));
  }

  async function confirm(password: string) {
    setError(null);
    setInfo(null);
    if (pending === 'trial' && plan !== 'NONE') {
      const session = await api<{ sessionId: string; redirectUrl: string }>('/subscription/checkout', {
        method: 'POST',
        token: true,
        body: { returnUrl: window.location.origin + '/settings', plan, interval, addOns, password, aiTier: 'NONE', aiPacks: [], fullTrial: true },
      });
      follow(session.redirectUrl);
    } else if (pending === 'subscribe') {
      const session = await api<{ sessionId: string; redirectUrl: string }>('/subscription/checkout', {
        method: 'POST',
        token: true,
        body: { returnUrl: window.location.origin + '/settings', plan, interval, addOns, password, promoCode: promo?.code,
          aiTier: aiOn ? aiTier : 'NONE', aiPacks: aiOn ? packList : [] },
      });
      follow(session.redirectUrl);
    } else if (pending === 'packs') {
      const session = await api<{ sessionId: string; redirectUrl: string }>('/subscription/checkout', {
        method: 'POST',
        token: true,
        body: { returnUrl: window.location.origin + '/account/subscription', plan: 'NONE', interval, addOns: [], password, aiTier: 'NONE', aiPacks: packList },
      });
      follow(session.redirectUrl);
    } else if (pending === 'ai') {
      const s = await api<SubscriptionStatus>('/subscription/ai', { method: 'POST', token: true, body: { aiTier, password } });
      setStatus(s);
      refreshAiAccess();
      const info = aiTier === 'NONE' ? null : aiInfo(aiTier);
      setInfo(info ? t('You now have {plan}.', { plan: t(info.label) }) : t('EasyLearning AI is removed from your subscription.'));
    } else if (pending === 'change' && plan !== 'NONE') {
      const result = await api<{ plan: string; immediate: boolean; effectiveFromEpochMs: number | null }>(
        '/subscription/plan', { method: 'POST', token: true, body: { plan, password } });
      const label = PLAN_LABEL[result.plan as PlanCode];
      const name = label ? t(label) : result.plan;
      const when = result.effectiveFromEpochMs ? new Date(result.effectiveFromEpochMs).toLocaleDateString() : t('your renewal date');
      setInfo(result.immediate ? t('You are now on {plan}.', { plan: name }) : t('{plan} starts on {date}.', { plan: name, date: when }));
      await load();
    } else if (pending === 'addons') {
      const s = await api<SubscriptionStatus>('/subscription/addons', { method: 'POST', token: true, body: { addOns, password } });
      setStatus(s);
      refreshEntitlements();
      setInfo(t('Your add-ons are updated.'));
    } else if (pending === 'cycle') {
      const s = await api<SubscriptionStatus>('/subscription/interval', { method: 'POST', token: true, body: { interval: nextCycle, password } });
      setStatus(s);
      const from = s.pendingPlanFromEpochMs ? new Date(s.pendingPlanFromEpochMs).toLocaleDateString() : t('your renewal date');
      setInfo(s.pendingInterval
        ? t('From {date} you will pay {cycle}.', { date: from, cycle: cycleLabel(nextCycle) })
        : t('You will keep paying {cycle}.', { cycle: cycleLabel(nextCycle) }));
    } else if (pending === 'portal') {
      const session = await api<{ sessionId: string; redirectUrl: string }>('/subscription/portal', {
        method: 'POST', token: true, body: { returnUrl: window.location.origin + '/settings', password },
      });
      follow(session.redirectUrl);
    }
    setPending(null);
  }

  if (loading && !status) {
    return <div className="card"><h2>{t('Subscription')}</h2><p className="muted">{t('Loading…')}</p></div>;
  }

  const code = status?.status ?? '';
  const isActive = code === 'ACTIVE';
  const isTrialing = code === 'TRIALING';
  const periodEnd = status?.currentPeriodEndEpochMs ?? null;
  const formattedEnd = periodEnd ? new Date(periodEnd).toLocaleDateString() : null;
  const addOnsChanged = isActive && status && [...addOns].sort().join() !== [...status.addOns].sort().join();
  const planChanged = isActive && status?.mainPlan && plan !== 'NONE' && plan !== status.plan;
  const renewalTotal = (cycle: Interval): number | null => {
    if (!status) return null;
    let sum = 0;
    if (status.mainPlan) {
      const o = offerFor(status.pendingPlan ?? status.plan, cycle);
      if (!o) return null;
      sum += o.unitAmount;
    }
    for (const a of status.addOns) {
      const o = offerFor(a, cycle);
      if (!o) return null;
      sum += o.unitAmount * Math.max(1, status.seats);
    }
    if (aiCurrent !== 'NONE') {
      const o = offerFor(aiCurrent, cycle);
      if (!o) return null;
      sum += o.unitAmount;
    }
    return sum;
  };
  const aiChanged = isActive && aiOn && aiTier !== aiCurrent;
  const scheduledCycle = status?.pendingInterval ?? status?.interval ?? 'MONTHLY';
  const cycleChanged = isActive && nextCycle !== scheduledCycle;
  const movingUp = planChanged && (offerFor(plan, interval)?.maxLearners ?? 0) > (status?.maxLearners ?? 0);

  const addOnList = (
    <fieldset className="sub-group">
      <legend className="field-label">{t('Add-ons')}</legend>
      {ADD_ONS.map((a) => {
        const o = offerFor(a, interval);
        const blocked = ADD_ON_INFO[a].needsMain && plan === 'NONE';
        const required = plan === 'NONE' && a === 'TALK_BOARD';
        return (
          <label key={a} className={`plan-option${addOns.includes(a) || required ? ' is-selected' : ''}${blocked || !o ? ' is-disabled' : ''}`}>
            <input
              type="checkbox"
              checked={addOns.includes(a) || required}
              disabled={required || blocked || !o || busy}
              onChange={() => setAddOns((cur) => (cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]))}
            />
            <span className="plan-option-body">
              <span className="plan-option-head">
                <span className="plan-name">{t(ADD_ON_INFO[a].name)}</span>
                <span className="plan-price">{o
                  ? (planPlaces
                    ? t('{price} per place on your plan / {every}', { price: money(o.unitAmount, o.currency), every })
                    : t('{price} per learner / {every}', { price: money(o.unitAmount, o.currency), every }))
                  : t('Not available yet')}</span>
              </span>
              <span className="plan-covers">{t(ADD_ON_INFO[a].describes)}{blocked ? ' ' + t('Needs a plan.') : ''}{required ? ' ' + t('Included with Talk Board only.') : ''}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );

  const aiList = aiOn && (
    <fieldset className="sub-group" id="ai">
      <legend className="field-label">EasyLearning AI</legend>
      {(ai?.developer || !!ai?.balance.tier || (ai?.balance.available ?? 0) > 0) && <CreditsBar access={ai} shop />}
      <div className="plan-list" role="radiogroup" aria-label="EasyLearning AI">
        <label className={`plan-option${aiTier === 'NONE' ? ' is-selected' : ''}`}>
          <input type="radio" name="ai-tier" checked={aiTier === 'NONE'} disabled={busy} onChange={() => setAiTier('NONE')} />
          <span className="plan-option-body">
            <span className="plan-option-head"><span className="plan-name">{t('No EasyLearning AI')}</span></span>
          </span>
        </label>
        {aiTiersHere.map((o) => {
          const info = aiInfo(o.product);
          return (
            <label key={o.product} className={`plan-option${aiTier === o.product ? ' is-selected' : ''}`}>
              <input type="radio" name="ai-tier" checked={aiTier === o.product} disabled={busy} onChange={() => setAiTier(o.product as AiCode)} />
              <span className="plan-option-body">
                <span className="plan-option-head">
                  <span className="plan-name">{t(info?.label ?? o.product)}</span>
                  <span className="plan-price">{money(o.unitAmount, o.currency)} / {every}</span>
                </span>
                <span className="plan-covers">
                  {t('{credits} credits every month. Unused ones do not carry over.', { credits: info?.credits ?? 0 })}
                  {isActive && o.product === aiCurrent && ' · ' + t('your AI plan')}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      <p className="field-hint" style={{ margin: 0 }}>{t('Studio, teaching advice, what to teach next and the Talk Board helper use credits. Each request uses what it actually cost.')}</p>
    </fieldset>
  );

  const packChoices = aiOn && packOffers.length > 0 && (
    <fieldset className="sub-group">
      <legend className="field-label">{t('Extra credits')}</legend>
      <p className="field-hint" style={{ margin: 0 }}>{t('Paid once. Extra credits do not run out, and they are used after the monthly ones.')}</p>
      {packOffers.map((p) => (
        <div key={p.id} className={`plan-option sub-pack${(packs[p.id] ?? 0) > 0 ? ' is-selected' : ''}`}>
          <span className="plan-option-body">
            <span className="plan-option-head">
              <span className="plan-name">{t(p.label)}</span>
              <span className="plan-price">{money(p.pence, 'gbp')}</span>
            </span>
          </span>
          <label className="sub-pack-qty">
            <span className="visually-hidden">{t('How many of {pack}', { pack: t(p.label) })}</span>
            <select value={packs[p.id] ?? 0} disabled={busy} onChange={(e) => setPacks((cur) => ({ ...cur, [p.id]: Number(e.target.value) }))}>
              {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
      ))}
    </fieldset>
  );

  const bigger = (
    <p className="field-hint" style={{ margin: 0 }}>
      {t('Need more than 20 learners? Contact EasyLearning at')} <a href="mailto:support@fractalon.net">support@fractalon.net</a>.
    </p>
  );

  return (
    <div className="card">
      <h2>{t('Subscription')}</h2>
      <div className="banner" role="note">
        {t('Why prices are split: we keep the main plan as low as we can and sell extras separately, such as your own pictures and voice, the Talk Board, extra learner places and EasyLearning AI. That way you only pay for what you use. Each extra adds to the total, so check the total before you pay.')}
      </div>
      {error && <div className="error" role="alert">{error}</div>}
      {info && <div className="banner" role="status">{info}</div>}

      {offers.length === 0 ? (
        <>
          <p style={{ marginTop: 0 }}>{t('We cannot show the prices just now. Try again in a moment.')}</p>
          <p className="muted">{t('Status:')} <strong>{STATUS_LABEL[code] ? t(STATUS_LABEL[code]) : code}</strong>{isTrialing && periodEnd && <> · {describeRemaining(periodEnd)}</>}</p>
          <div className="toolbar"><button className="btn ghost" onClick={() => void load()} disabled={busy}>{t('Try again')}</button></div>
        </>
      ) : isActive && status ? (
        <>
          <p style={{ marginTop: 0 }}>
            {status.mainPlan
              ? <>{t('You are on')} <strong>{PLAN_LABEL[status.plan as PlanCode] ? t(PLAN_LABEL[status.plan as PlanCode]) : status.plan}</strong> · {cap(status.maxLearners)}</>
              : <>{t('You have')} <strong>{status.addOns.map((a) => t(ADD_ON_INFO[a].name)).join(' ' + t('and') + ' ')}</strong> {t('without a plan')}</>}
            {' · '}{t('paid {cycle}', { cycle: cycleLabel(status.interval) })}
            {formattedEnd && <> · {t('renews {date}', { date: formattedEnd })}</>}
          </p>

          {status.pendingPlan && (
            <p className="banner" role="status">
              <strong>{PLAN_LABEL[status.pendingPlan as PlanCode] ? t(PLAN_LABEL[status.pendingPlan as PlanCode]) : status.pendingPlan}</strong>
              {' '}{status.pendingPlanFromEpochMs ? t('starts on {date}.', { date: new Date(status.pendingPlanFromEpochMs).toLocaleDateString() }) : t('starts at your next renewal.')}
            </p>
          )}

          {status.pendingInterval && (
            <p className="banner" role="status">
              {t('From {date} you will pay', { date: status.pendingPlanFromEpochMs ? new Date(status.pendingPlanFromEpochMs).toLocaleDateString() : t('your next renewal') })} <strong>{cycleLabel(status.pendingInterval)}</strong>
              {renewalTotal(status.pendingInterval) !== null && <> ({t('{price} every {every}', { price: money(renewalTotal(status.pendingInterval)!, offers[0]?.currency ?? 'gbp'), every: everyOf(status.pendingInterval) })})</>}.
            </p>
          )}

          <fieldset className="sub-group">
            <legend className="field-label">{t('How often to pay')}</legend>
            <div className="sub-cycles" role="radiogroup" aria-label={t('How often to pay')}>
              {cyclesOffered.map((c) => {
                const cost = renewalTotal(c.value);
                return (
                  <label key={c.value} className={`sub-cycle${nextCycle === c.value ? ' is-selected' : ''}${cost === null ? ' is-disabled' : ''}`}>
                    <input type="radio" name="next-cycle" checked={nextCycle === c.value} disabled={cost === null || busy} onChange={() => setNextCycle(c.value)} />
                    <span>{t(c.label)}{c.value === status.interval ? ' ' + t('(now)') : ''}</span>
                    {cost !== null && <span className="sub-cycle-price">{money(cost, offers[0]?.currency ?? 'gbp')} / {t(c.every)}</span>}
                    {c.saving && <span className="sub-saving">{t(c.saving)}</span>}
                  </label>
                );
              })}
            </div>
            <p className="field-hint" style={{ margin: 0 }}>
              {formattedEnd
                ? t('A change starts when your subscription renews on {date}. You keep everything you have already paid for.', { date: formattedEnd })
                : t('A change starts when your subscription renews. You keep everything you have already paid for.')}
            </p>
          </fieldset>

          {status.mainPlan && (
            <fieldset className="sub-group">
              <legend className="field-label">{t('Subscription plan')}</legend>
              <div className="plan-list" role="radiogroup" aria-label={t('Plans')}>
                {plansHere.map((p) => (
                  <label key={p.product} className={`plan-option${plan === p.product ? ' is-selected' : ''}`}>
                    <input type="radio" name="plan" checked={plan === p.product} onChange={() => setPlan(p.product as PlanCode)} />
                    <span className="plan-option-body">
                      <span className="plan-option-head">
                        <span className="plan-name">{t(PLAN_LABEL[p.product as PlanCode])}</span>
                        <span className="plan-price">{money(p.unitAmount, p.currency)} / {every}</span>
                      </span>
                      <span className="plan-covers">{cap(p.maxLearners ?? 1)}{p.product === status.plan && ' · ' + t('your plan')}</span>
                    </span>
                  </label>
                ))}
              </div>
              {bigger}
            </fieldset>
          )}

          {addOnList}

          {aiList}

          {packChoices}

          {packTotal > 0 && (
            <div className="sub-summary" aria-live="polite">
              {packLines.map((l) => (
                <div key={l.label} className="sub-line"><span>{l.label}</span><span>{money(l.amount, l.currency)}</span></div>
              ))}
              <div className="sub-line sub-total"><span>{t('Total, paid once')}</span><span>{money(packTotal, 'gbp')}</span></div>
            </div>
          )}

          <div className="form-actions">
            {aiChanged && (
              <button className="btn" onClick={() => setPending('ai')} disabled={busy}>{t('Save EasyLearning AI')}</button>
            )}
            {packTotal > 0 && (
              <button className="btn" onClick={() => setPending('packs')} disabled={busy}>{t('Buy extra credits')}</button>
            )}
            {planChanged && (
              <button className="btn" onClick={() => setPending('change')} disabled={busy}>
                {movingUp
                  ? t('Switch to {plan} now', { plan: t(PLAN_LABEL[plan as PlanCode]) })
                  : t('Switch to {plan} at renewal', { plan: t(PLAN_LABEL[plan as PlanCode]) })}
              </button>
            )}
            {addOnsChanged && (
              <button className="btn" onClick={() => setPending('addons')} disabled={busy}>{t('Save add-ons')}</button>
            )}
            {cycleChanged && (
              <button className="btn" onClick={() => setPending('cycle')} disabled={busy}>
                {nextCycle === status.interval
                  ? t('Keep paying {cycle}', { cycle: cycleLabel(nextCycle) })
                  : t('Pay {cycle} from renewal', { cycle: cycleLabel(nextCycle) })}
              </button>
            )}
            <button className="btn secondary" onClick={() => setPending('portal')} disabled={busy}>{t('Manage payment or cancel')}</button>
          </div>
          {aiChanged && (
            <p className="field-hint">{t('Adding or changing EasyLearning AI charges now for what is left of the current billing period; removing it stops it straight away.')}</p>
          )}
          {addOnsChanged && (
            <p className="field-hint">{t('Add-ons are charged for every place on your plan, or per learner without a plan. Adding one charges the part of this {every} that is left; removing one stops it straight away.', { every })}</p>
          )}
        </>
      ) : (
        <>
          <p style={{ marginTop: 0 }}>
            {t('Choose how often to pay, a plan, and any add-ons. Everything renews automatically until you cancel.')}
          </p>

          <fieldset className="sub-group">
            <legend className="field-label">{t('How often to pay')}</legend>
            <div className="sub-cycles" role="radiogroup" aria-label={t('How often to pay')}>
              {cyclesOffered.map((c) => (
                <label key={c.value} className={`sub-cycle${interval === c.value ? ' is-selected' : ''}`}>
                  <input type="radio" name="cycle" checked={interval === c.value} onChange={() => setInterval(c.value)} />
                  <span>{t(c.label)}</span>
                  {c.saving && <span className="sub-saving">{t(c.saving)}</span>}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="sub-group">
            <legend className="field-label">{t('Subscription plan')}</legend>
            <div className="plan-list" role="radiogroup" aria-label={t('Plans')}>
              {plansHere.map((p) => (
                <label key={p.product} className={`plan-option${plan === p.product ? ' is-selected' : ''}`}>
                  <input type="radio" name="plan" checked={plan === p.product} onChange={() => setPlan(p.product as PlanCode)} />
                  <span className="plan-option-body">
                    <span className="plan-option-head">
                      <span className="plan-name">{t(PLAN_LABEL[p.product as PlanCode])}</span>
                      <span className="plan-price">{money(p.unitAmount, p.currency)} / {every}</span>
                    </span>
                    <span className="plan-covers">{cap(p.maxLearners ?? 1)}</span>
                  </span>
                </label>
              ))}
              {offerFor('TALK_BOARD', interval) && (
                <label className={`plan-option${plan === 'NONE' ? ' is-selected' : ''}`}>
                  <input type="radio" name="plan" checked={plan === 'NONE'} onChange={() => { setPlan('NONE'); setAddOns(['TALK_BOARD']); }} />
                  <span className="plan-option-body">
                    <span className="plan-option-head"><span className="plan-name">{t('No plan — Talk Board only')}</span></span>
                    <span className="plan-covers">{t('Just the communication board, without the learning programs.')}</span>
                  </span>
                </label>
              )}
            </div>
            {bigger}
          </fieldset>

          {addOnList}

          {aiList}

          {packChoices}

          {(lines.length > 0 || packTotal > 0) && (
            <div className="sub-summary" aria-live="polite">
              {[...lines, ...packLines].map((l) => (
                <div key={l.label} className="sub-line"><span>{l.label}</span><span>{money(l.amount, l.currency)}</span></div>
              ))}
              {promo && (
                <div className="sub-line"><span>{t('Promotion {code} ({percent}% off the first payment)', { code: promo.code, percent: promo.percentOff })}</span><span>−{money(discount, currency)}</span></div>
              )}
              {promo || packTotal > 0 ? (
                <>
                  <div className="sub-line sub-total"><span>{t('First payment')}</span><span>{money(firstPayment - discount, currency)}</span></div>
                  {total > 0 && <div className="sub-line"><span>{t('Then every {every}', { every })}</span><span>{money(total, currency)}</span></div>}
                </>
              ) : (
                <div className="sub-line sub-total"><span>{t('Total every {every}', { every })}</span><span>{money(total, currency)}</span></div>
              )}
            </div>
          )}

          {(promosRunning || promo) && (
            <div className="field sub-promo">
              <label htmlFor="sub-promo">{t('Promotion code')}</label>
              <div className="sub-promo-row">
                <input
                  id="sub-promo"
                  value={promoInput}
                  maxLength={40}
                  autoComplete="off"
                  onChange={(e) => { setPromoInput(e.target.value); setPromoError(null); if (promo) setPromo(null); }}
                />
                {promo
                  ? <button type="button" className="btn secondary" onClick={() => { setPromo(null); setPromoInput(''); }}>{t('Remove')}</button>
                  : <button type="button" className="btn secondary" disabled={!promoInput.trim() || checkingPromo} onClick={() => void applyPromo()}>{checkingPromo ? t('Checking…') : t('Apply')}</button>}
              </div>
              {promoError && <span className="error" role="alert">{promoError}</span>}
              {promo && <span className="field-hint" role="status">{t('{percent}% off your first payment. Renewals are full price.', { percent: promo.percentOff })}</span>}
            </div>
          )}

          <p className="muted">
            {t('Status:')} <strong>{STATUS_LABEL[code] ? t(STATUS_LABEL[code]) : code}</strong>
            {isTrialing && periodEnd && <> · {describeRemaining(periodEnd)}</>}
            {!isTrialing && formattedEnd && <> · {t('access until {date}', { date: formattedEnd })}</>}
          </p>

          {status?.limitedTrial && plan !== 'NONE' && (
            <div className="sub-trial" role="note">
              <p style={{ margin: 0 }}><strong>{t('You are on the limited trial.')}</strong> {t('The full 7-day trial has every program, every game type and your own photos and voice.')}</p>
              <p className="field-hint" style={{ margin: 0 }}>{t('A card is needed to start it. Nothing is taken until the 7 days end, and you can cancel before then. A card that has already had a trial is charged straight away.')}</p>
            </div>
          )}

          <div className="form-actions">
            {status?.limitedTrial && plan !== 'NONE' && (
              <button className="btn" onClick={() => setPending('trial')} disabled={busy}>{t('Start your full 7-day trial')}</button>
            )}
            <button className={status?.limitedTrial && plan !== 'NONE' ? 'btn secondary' : 'btn'} onClick={() => setPending('subscribe')} disabled={busy || (lines.length === 0 && packTotal === 0)}>{lines.length === 0 ? t('Buy extra credits') : t('Subscribe')}</button>
          </div>
        </>
      )}

      <StorageUsage />

      <p className="field-hint" style={{ marginTop: 'var(--s4)', marginBottom: 0 }}>
        {t('Pay by card or by Direct Debit from a UK bank account. Payments are handled by Stripe; we never see or store your card or bank details. A Direct Debit takes a few working days to clear, and you can use your subscription straight away.')}
      </p>
      <StripeLanguageNote />


      <PasswordDialog
        open={pending !== null}
        title={pending === 'portal' ? t('Open your billing details') : pending === 'subscribe' ? t('Confirm your subscription') : pending === 'trial' ? t('Start your full trial') : pending === 'packs' ? t('Buy extra credits') : t('Confirm the change')}
        message={
          pending === 'portal' ? t('Enter your password to open your billing details at Stripe, where you can change your card or cancel.')
            : pending === 'trial' ? t('Enter your password, then you will be taken to Stripe to add your card. Nothing is taken until the trial ends.')
            : pending === 'subscribe' || pending === 'packs' ? t('Enter your password, then you will be taken to Stripe to pay.')
              : t('Enter your password to confirm this change.')
        }
        confirmLabel={pending === 'portal' ? t('Continue') : pending === 'trial' ? t('Continue to add a card') : pending === 'subscribe' || pending === 'packs' ? t('Continue to payment') : t('Confirm change')}
        onConfirm={confirm}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
